import React, { useRef, useCallback } from "react";
import {
  ClockIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

/**
 * OTP (One-Time Password) input component with digit boxes.
 * Reusable for Signup, VerifyEmail, ForgotPassword, and other OTP flows.
 * @see .cursor/ui.md
 *
 * @param {string} value - Current OTP value (controlled)
 * @param {function} onChange - Called with synthetic event { target: { name, value } } for Formik
 * @param {function} onBlur - Optional blur callback (e.g. for Formik setFieldTouched)
 * @param {string} name - Form field name
 * @param {number} length - Number of digit boxes (default 6)
 * @param {string} error - Error message to display
 * @param {number} resendTimer - Seconds until resend is available (0 = can resend)
 * @param {function} onResend - Callback when user clicks Resend
 * @param {string} label - Label text (default "Verification code")
 * @param {boolean} disabled - Disable input
 * @param {string} className - Wrapper class
 */
const OTPInput = ({
  value = "",
  onChange,
  onBlur,
  name = "otp",
  length = 6,
  error,
  resendTimer = 0,
  onResend,
  label = "Verification code",
  disabled = false,
  className = "",
}) => {
  const inputRefs = useRef([]);

  const digits = value.split("").concat(Array(length).fill("")).slice(0, length);

  const focusInput = useCallback((index) => {
    const ref = inputRefs.current[index];
    if (ref) ref.focus();
  }, []);

  const handleChange = (index, digit) => {
    const sanitized = digit.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = sanitized;
    const newValue = newDigits.join("").slice(0, length);
    onChange?.({ target: { name, value: newValue } });
    if (sanitized && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
      const newDigits = [...digits];
      newDigits[index - 1] = "";
      onChange?.({ target: { name, value: newDigits.join("").slice(0, length) } });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted) {
      onChange?.({ target: { name, value: pasted } });
      const nextEmpty = Math.min(pasted.length, length - 1);
      focusInput(nextEmpty);
    }
  };

  const baseBoxClass =
    "flex-1 min-w-0 h-14 sm:h-16 text-center text-xl font-semibold border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
  const defaultBoxClass = "border-gray-300 text-gray-900";
  const errorBoxClass = "border-red-500 focus:border-red-500 focus:ring-red-500/20";

  return (
    <div className={`w-full ${className}`.trim()}>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label} <span className="text-red-500">*</span>
      </label>
      <div
        className="flex gap-2 w-full"
        onPaste={handlePaste}
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onBlur={() => onBlur?.({ target: { name } })}
            disabled={disabled}
            className={`${baseBoxClass} ${error ? errorBoxClass : defaultBoxClass} ${
              disabled ? "bg-gray-50 cursor-not-allowed" : ""
            }`}
            aria-label={`Digit ${index + 1} of ${length}`}
          />
        ))}
      </div>
      {error && (
        <p className="mt-1.5 text-xs font-medium text-red-600 text-center">
          {error}
        </p>
      )}
      {onResend != null && (
        <div className="mt-3 text-center">
          {resendTimer > 0 ? (
            <div className="flex items-center justify-center gap-2 text-sm">
              <ClockIcon className="h-4 w-4 text-gray-400 animate-pulse" />
              <span className="text-gray-500 font-medium">
                Resend OTP in {resendTimer}s
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onResend}
              className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark disabled:text-gray-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
            >
              <ArrowPathIcon className="h-4 w-4" />
              <span>Resend OTP</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OTPInput;
